import React, { useEffect, useState } from 'react';

import GoogleMapReact, { Maps } from 'google-map-react';
import { Stage } from '../features/pilgrimage/stage';
import { currentStage } from '../features/pilgrimage/current-stage';
import { PlaceMarker } from './PlaceMarker';
import { useSelector } from 'react-redux';
import { selectSum } from '../features/log/logSlice';
import { LatLng, latLngToDistance } from '../utils/lat-lng-to-distance';
import { selectStages } from '../features/pilgrimage/pilgrimageSlice';
import { useOnline } from '../app/use-online';
import { IonCard, IonCardContent, IonCardHeader, IonCardTitle } from '@ionic/react';

type ProgressMapProps = {
  stage: Stage;
}

function distanceToZoomLevel(distance: number) {
  if(distance <= 9) {
    return 12;
  } else if(distance <= 40) {
    return 11;
  } else {
    return 10;
  }
}

export const ProgressMap = ({ stage } : ProgressMapProps) => {
  const sum = useSelector(selectSum),
    stages = useSelector(selectStages),
    { remainder } = currentStage(sum, stages || []) || {},
    traveledSoFar = remainder ? stage.distance - remainder : null,
    online = useOnline();

  const [directions, setDirections] = useState<any | null>(null), //useState<google.maps.DirectionsResult | null>(null),
    [map, setMap] = useState<google.maps.Map | null>(null);

  // When the Google API is loaded, set API handles
  const handleApiLoaded = (googleMap : google.maps.Map, maps : Maps) => {
    setMap(googleMap);
  }

  const fetchDirectionsForStage = (stage : Stage) => {
    const fetchData = async () => {
      if(stage.directions) {
        const resp = await fetch(stage.directions),
          json = await resp.json();
        setDirections(json);
      }
    }
    fetchData();
  };

  // When stage changes, ask for directions again
  useEffect(() => fetchDirectionsForStage(stage), [stage]);

  // Effect draws polylines after each render
  useEffect(() => {
    let polyline : google.maps.Polyline;
    let remainingPolyline : google.maps.Polyline;

    console.log('draw directions? ', map, directions, traveledSoFar)

    if(map && directions && Number(traveledSoFar) >= 0) {
      polyline = new google.maps.Polyline({
        path: [],
        strokeColor: '#33CC33',
        strokeWeight: 6,
        strokeOpacity: 0.75
      });
  
      remainingPolyline = new google.maps.Polyline({
        path: [],
        strokeColor: '#CCCC33',
        strokeWeight: 6,
        strokeOpacity: 0.75
      });
  
      const bounds = new google.maps.LatLngBounds();
  
      let incrementalDistance = 0;
  
      let prevPt : null | LatLng = null,
        currentPt : null | LatLng = null,
        pastHowFarWeveGone = false;
  
      const legs = directions.routes[0].legs;
      for (let i = 0; i < legs.length; i++) {
        const steps = legs[i].steps;
        for (let j = 0; j < steps.length; j++) {
          const nextSegment = steps[j].path;
  
          for (let k = 0; k < nextSegment.length; k++) {
            prevPt = currentPt;
            currentPt = nextSegment[k];
            if(prevPt && currentPt) {
              const nextSegmentDistance = latLngToDistance(prevPt, currentPt);
              if(incrementalDistance + nextSegmentDistance >= Number(traveledSoFar)) {
                pastHowFarWeveGone = true;
              }
              if(!pastHowFarWeveGone) {
                polyline.getPath().push(new google.maps.LatLng(currentPt));
                bounds.extend(currentPt);
                incrementalDistance += nextSegmentDistance;
              } else {
                remainingPolyline.getPath().push(new google.maps.LatLng(currentPt));
                bounds.extend(currentPt);
                incrementalDistance += nextSegmentDistance;
              }
            }
          }
        }
      }
  
      polyline.setMap(map);
      remainingPolyline.setMap(map);
    }

    return () => {
      if(polyline) {
        polyline.setMap(null);
      }
      if(remainingPolyline) {
        polyline.setMap(null);
      }
    }
  }, [map, directions, traveledSoFar]);

  // Render component
  return (
    online
    ? <GoogleMapReact
      bootstrapURLKeys={{ key: 'AIzaSyB_TB9wPJBiwG8kG_Y_YletyIqYoCL-g0Q' }}
      center={{
        lat: (stage.start.lat + stage.end.lat) /2,
        lng: (stage.start.lng + stage.end.lng) /2
      }}
      zoom={distanceToZoomLevel(stage.distance)}
      options={{
        gestureHandling: 'cooperative'
      }}
      yesIWantToUseGoogleMapApiInternals
      onGoogleApiLoaded={({ map, maps }) => handleApiLoaded(map, maps)}
    >
      <PlaceMarker { ... stage.start} />
      <PlaceMarker { ... stage.end} />
    </GoogleMapReact>
    : <IonCard>
      <IonCardHeader>
        <IonCardTitle>Device is Offline</IonCardTitle>
      </IonCardHeader>
      <IonCardContent>
        <p>It looks like you’re not connected to the Internet. We’ll display a map of the route when you’re back online.</p>
      </IonCardContent>
    </IonCard>
  )
}